import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Card, Image, Icon, Loader, Checkbox, Grid, Button } from "semantic-ui-react";
import BCButton from "../ui/BCButton";
import BCMessage from "../ui/BCMessage";
import BCKanban from "../ui/BCKanban";
import BCQuoteIcon from "../../assets/bc-quote-icon.png";
import BCTable from "../ui/BCTable";
import BCMenuBar from "../ui/BCMenuBar";
import BCFilterSearch from "../BCFilterSearch";
import { BCFilterBar } from "../BCFilter";
import { exportWorkbookFromApplication, getApplicationInfoList, getBorrowerApplicationInfo } from "../../api/BrokerApi";
import { exportMockWorkbookFromApplication, getMockApplicationInfo, getMockApplicationInfolist } from "../../api/mock/MockBrokerApi";
import { createFile, guid, handleErrorResponse, isValidPermission, setBackgroundColor } from "../../Helpers";
import "../../styles/landing-page.scss";
import "../../styles/main.scss";
import BCCards from "../ui/BCCards";
import { convertArrayToOxfordString, useBCGlobalFilter } from "../../helpers/GlobalHelper";
import { GlobalConstants, Personas } from "../constants/GlobalConstants";
import { getIsWhiteLabeled, whoAmI } from "../../helpers/PermissionHelper";
import { FeatureFlags } from "../constants/LocalServerConstants";
import BCModal from "../ui/BCModal";

const BPApplications = ({ 
  isGreaterThanMobile,
  isGreaterThanTablet,
  isSidebar,
  applicationInfolist,
  setApplicationInfolist,
  isMessage,
  setIsMessage,
  errorResponse,
  setErrorResponse,
  user,
  proxyUser,
  permissions,
  brokerColor,
  setCopiedApplicationId
 }) => {
  const [isFirstLoad, setIsFirstLoad] = useState(true as boolean);
  const [isLoading, setIsLoading] = useState(true);
  const [isKanbanView, setisKanbanView] = useState(localStorage.getItem("infolist") === "table" ? false : true);
  const [kanbanViews, setKanbanViews] = useState([] as any[]);
  const [isFetching, setIsFetching] = useState(false);
  const [currentSliceTo, setCurrentSliceTo] = useState(GlobalConstants.DefaultValues.defaultSliceKanban);
  const [exportWorkbookUrl, setExportWorkbookUrl] = useState(undefined as any);
  const exportWorkbookRef = useRef<HTMLElement>(null);
  // TODO: Make Borrower Application Constants with Ids for Steps
  const BAProjectSectionCompleteId = "d0c9e9d7-7c1e-45c3-bcac-05e3f3283d47";
  const BAPropertySectionCompleteId = "41dba333-7fa8-4ba1-ba25-06a686a76e45";
  const BABudgetSectionCompleteId = "b25dc933-8eb3-4844-8ca7-733a850a42a3";
  const [isExportSectionIncompleteModal, setIsExportSectionIncompleteModal] = useState(false as boolean);
  const [invalidExportSections, setInvalidExportSections] = useState([] as string[]);
  const [exportSectionApplicationId, setExportSectionApplicationId] = useState("");
  const BPApplicationKanbanLanes = 
  {
    New: {
      name: "In Progress",
      index: 0
    },
    Sent: {
      name: "Invitation Sent",
      index: 1
    },
    Revision: {
      name: "Requires Revision",
      index: 2
    },
    Submitted: {
      name: "Submitted for Review",
      index: 3
    },
  };
  const reducerInstance = useBCGlobalFilter(applicationInfolist, { hiddenFilters: [], filterKeysList: ["loanProgram", "brokerage", "brokerEmail", "name"] });
  const [{ isFilterOpen, getFilteredData, filteredData: filteredApplicationData, filterBySearchValue }]: any = reducerInstance;
  const history = useHistory();
  const persona = whoAmI(user, proxyUser);
  const isStartApplicationPersona = [
    Personas.SALES_ADMIN,
    Personas.BROKER_ADMIN,
    Personas.LOAN_OFFICER,
    Personas.BRM,
    Personas.BROKER,
    Personas.NWL_GUARANTOR,
    Personas.WL_GUARANTOR
  ].indexOf(persona) > -1;

  const isInternalUser = [
    Personas.SYSTEM_ADMIN,
    Personas.SALES_ADMIN,
    Personas.BROKER_ADMIN,
    Personas.LOAN_OFFICER,
    Personas.BRM
  ].indexOf(persona) > -1;

  const handleSummary = useCallback((applicationInfolist, id) => {
    const foundApplication = applicationInfolist.find(x => x.id === id);
    if (foundApplication && foundApplication.submittedOn) {
      if (FeatureFlags.IS_GAME_CHANGERS) {
        history.push(`/application/tasks/${id}`);
      }
      else if (FeatureFlags.IS_BC_PRICING) {
        history.push(`/application/pricing/${id}`);
      }
      else {
        history.push(`/application/project/${id}`);
      }
    }
    else {
      history.push(`/application/borrower/${id}`);
    }
  }, [history, BPApplicationKanbanLanes]);

  const handleError = useCallback(() => {
    history.push("/error");
  }, [history]);

  const handleApplication = useCallback((applicationObj) => {
    setCopiedApplicationId(undefined);
    history.push(`/application/borrower/${applicationObj.id ? applicationObj.id : guid()}`);
  }, [history, setCopiedApplicationId]);

  const handleCopyApplication = useCallback((applicationObj) => {
    setCopiedApplicationId(applicationObj.id);
    history.push(`/application/borrower/${guid()}`);
  }, [history, setCopiedApplicationId]);

  const getFilteredOrEmptyData = (kanbanData) => {
    if (!kanbanData) {
      return [];
    }

    return getFilteredData(kanbanData.cards);
  };

  const sortFilteredApplicationsKanban = () => {
    const kanbanView = [
      {
        index: BPApplicationKanbanLanes.New.index,
        name: getIsWhiteLabeled() ? "New" : BPApplicationKanbanLanes.New.name,
        title: Object.getOwnPropertyNames(BPApplicationKanbanLanes)[BPApplicationKanbanLanes.New.index],
        cards: filterBySearchValue(getFilteredOrEmptyData(kanbanViews[BPApplicationKanbanLanes.New.index])).slice(0, currentSliceTo)
      },
      {
        index: BPApplicationKanbanLanes.Sent.index,
        name: getIsWhiteLabeled() ? "In Progress" : BPApplicationKanbanLanes.Sent.name,
        title: Object.getOwnPropertyNames(BPApplicationKanbanLanes)[BPApplicationKanbanLanes.Sent.index],
        cards: filterBySearchValue(getFilteredOrEmptyData(kanbanViews[BPApplicationKanbanLanes.Sent.index])).slice(0, currentSliceTo)
      },
      {
        index: BPApplicationKanbanLanes.Revision.index,
        name: BPApplicationKanbanLanes.Revision.name,
        title: Object.getOwnPropertyNames(BPApplicationKanbanLanes)[BPApplicationKanbanLanes.Revision.index],
        cards: filterBySearchValue(getFilteredOrEmptyData(kanbanViews[BPApplicationKanbanLanes.Revision.index])).slice(0, currentSliceTo)
      },
      {
        index: BPApplicationKanbanLanes.Submitted.index,
        name: BPApplicationKanbanLanes.Submitted.name,
        title: Object.getOwnPropertyNames(BPApplicationKanbanLanes)[BPApplicationKanbanLanes.Submitted.index],
        cards: filterBySearchValue(getFilteredOrEmptyData(kanbanViews[BPApplicationKanbanLanes.Submitted.index])).slice(0, currentSliceTo)
      }
    ];

    if (persona === Personas.WL_GUARANTOR) {
      kanbanView.splice(0, 1);
    }

    return kanbanView;
  };

  const sortApplicationsKanban = (applicationData) => {
    const kanbanView = [
      {
        index: BPApplicationKanbanLanes.New.index,
        name: BPApplicationKanbanLanes.New.name,
        title: Object.getOwnPropertyNames(BPApplicationKanbanLanes)[BPApplicationKanbanLanes.New.index],
        cards: [] as any
      },
      {
        index: BPApplicationKanbanLanes.Sent.index,
        name: getIsWhiteLabeled() ? "In Progress" : BPApplicationKanbanLanes.Sent.name,
        title: Object.getOwnPropertyNames(BPApplicationKanbanLanes)[BPApplicationKanbanLanes.Sent.index],
        cards: [] as any
      },
      {
        index: BPApplicationKanbanLanes.Revision.index,
        name: BPApplicationKanbanLanes.Revision.name,
        title: Object.getOwnPropertyNames(BPApplicationKanbanLanes)[BPApplicationKanbanLanes.Revision.index],
        cards: [] as any
      },
      {
        index: BPApplicationKanbanLanes.Submitted.index,
        name: BPApplicationKanbanLanes.Submitted.name,
        title: Object.getOwnPropertyNames(BPApplicationKanbanLanes)[BPApplicationKanbanLanes.Submitted.index],
        cards: [] as any
      }
    ];

    applicationData.forEach((application) => {
      if (application.statusLookupName && application.name !== null) {
        let kanbanObjIndex = -1;
        if (FeatureFlags.IS_BETA && persona === Personas.WL_GUARANTOR && application.statusLookupName === BPApplicationKanbanLanes.New.name) {
          kanbanObjIndex = kanbanView.findIndex(x => x.title === Object.getOwnPropertyNames(BPApplicationKanbanLanes)[BPApplicationKanbanLanes.Sent.index]);
        }
        else if (application.statusLookupName === BPApplicationKanbanLanes.New.name) {
          kanbanObjIndex = kanbanView.findIndex(x => x.title === Object.getOwnPropertyNames(BPApplicationKanbanLanes)[BPApplicationKanbanLanes.New.index]);
        }
        else {
          kanbanObjIndex = kanbanView.findIndex(x => x.title === application.statusLookupName);
        }

        if (kanbanObjIndex !== -1) {
          kanbanView[kanbanObjIndex].cards.push(application);
        }
      }
    });

    return kanbanView;
  };

  const sortedApplicationsKanban = useMemo(() => sortFilteredApplicationsKanban(), [filteredApplicationData, currentSliceTo]);
  const convertToTableFormat = (applications) => {
    const result = [] as any[];
    applications.forEach((application) => {
      if (application.name !== null) {
        result.push({
          "id": application.id,
          "Name": application.name,
          "Loan Program": application.loanProgram,
          "Properties or Units": application.numberOfPropertiesOrUnits,
          "Status": application.statusLookupName,
          "Closing Date" : application.closingDate ? new Date(application.closingDate).toLocaleDateString() : null,
          "Submitted On": application.submittedOn ? new Date(application.submittedOn).toLocaleDateString() : null
        });
      }
    });

    return result;
  };

  const handleInfolistViewType = () => {
    const infolistStyle = isKanbanView ? "table" : "card";
    localStorage.setItem("infolist", infolistStyle);
    setisKanbanView(!isKanbanView);
  };

  const handleScroll = () => {
    if (window.innerHeight - document.documentElement.scrollTop !== document.documentElement.clientHeight) {
      setIsFetching(true);
    }
    else {
      return;
    }
  };

  const handleExportWorkbook = async (application) => {
    if (application.id) {
      setIsLoading(true);
      const borrowerApplicationInfo = await getBorrowerApplicationInfoAsync(application.id);
      if (borrowerApplicationInfo && borrowerApplicationInfo.screenStatus) {
        const applicationScreenStatus = JSON.parse(borrowerApplicationInfo.screenStatus);
        const applicationMaxScreenStatus = applicationScreenStatus.maxScreens;
        const currentInvalidExportSections = [] as string[];
        if (!applicationMaxScreenStatus.Projects || applicationMaxScreenStatus.Projects && applicationMaxScreenStatus.Projects !== BAProjectSectionCompleteId) {
          currentInvalidExportSections.push("Projects");
        }

        if (!applicationMaxScreenStatus.Property || applicationMaxScreenStatus.Property && applicationMaxScreenStatus.Property !== BAPropertySectionCompleteId) {
          currentInvalidExportSections.push("Property");
        }

        if (!applicationMaxScreenStatus.Budget || applicationMaxScreenStatus.Budget && applicationMaxScreenStatus.Budget !== BABudgetSectionCompleteId) {
          currentInvalidExportSections.push("Budget");
        }
        
        if (currentInvalidExportSections.length === 0) {
          await exportWorkbookAsync(application);
        }
        else {
          setExportSectionApplicationId(application.id);
          setInvalidExportSections(currentInvalidExportSections);
          setIsExportSectionIncompleteModal(true);
        }
      }

      setIsLoading(false);
    }
  };

  const getBorrowerApplicationInfoAsync = async (applicationId) => {
    try {
      const response = FeatureFlags.IS_MOCK ? await getMockApplicationInfo(applicationId) : await getBorrowerApplicationInfo(applicationId, proxyUser.id);
      if (response && response.ok) {
        const applicationInfo = await response.json();
        return applicationInfo;
      }
      else {
        await handleErrorResponse(response, setIsMessage, setErrorResponse);
      }
    }
    catch (error) {
      handleError();
    }

    return null;
  };

  const exportWorkbookAsync = async (application) => {
    try {
      const response = FeatureFlags.IS_MOCK ? await exportMockWorkbookFromApplication(application.id) : await exportWorkbookFromApplication(application.id, proxyUser.id);
      if (response && response.ok) {
        const data = await response.blob();
        const workbook = await createFile(data, "workbook", data.type);
        // TODO: Implement #14880
        // const fileURL = URL.createObjectURL(workbook);
        // const fileLink = document.createElement("a");
        // fileLink.href = fileURL;
        // fileLink.download = "Insert File Name";
        // fileLink.click();
        setExportWorkbookUrl(URL.createObjectURL(workbook));
        if (exportWorkbookRef.current) {
          exportWorkbookRef.current.click();
        }
      }
      else {
        await handleErrorResponse(response, setIsMessage, setErrorResponse);
      }
    }
    catch (error) {
      setIsMessage(false);
    }
  };

  useEffect(() => {
    const getApplicationInfoAsync = async () => {
      try {
        let brokerageOrganizationId = undefined as any;
        const brokerageObjString = localStorage.getItem("brokerage");
        if (brokerageObjString && brokerageObjString !== "") {
          const brokerageObj = JSON.parse(brokerageObjString);
          if (brokerageObj.id) {
            brokerageOrganizationId = brokerageObj.id;
          }
        }

        const response = FeatureFlags.IS_MOCK ? await getMockApplicationInfolist() : 
          brokerageOrganizationId ? await getApplicationInfoList(proxyUser.id, brokerageOrganizationId) :
          await getApplicationInfoList(proxyUser.id);
        if (response && response.ok) {
          const applicationInfolistData = await response.json();
          applicationInfolistData.forEach((applicationInfo) => {
            applicationInfo.amount = applicationInfo.loanCommitment;
            delete applicationInfo["loanCommitment"];
          });

          setIsMessage(false);
          setApplicationInfolist(applicationInfolistData);
          setKanbanViews(sortApplicationsKanban(applicationInfolistData));
        }
        else {
          await handleErrorResponse(response, setIsMessage, setErrorResponse);
        }
      }
      catch (error) {
        handleError();
      }
      setIsLoading(false);
    };

    if (isFirstLoad) {
      getApplicationInfoAsync();
    }
  }, [setIsMessage, setErrorResponse, setIsLoading, setApplicationInfolist, handleError, proxyUser, isFirstLoad, filteredApplicationData]);

  useEffect(() => {
    setIsFirstLoad(false);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!isFetching) {
      return;
    }

    setCurrentSliceTo(currentSliceTo + 5);
    setIsFetching(false);
  }, [isFetching]);

  useEffect(() => {
    if (currentSliceTo !== GlobalConstants.DefaultValues.defaultSliceKanban) {
      setCurrentSliceTo(GlobalConstants.DefaultValues.defaultSliceKanban);
    }
  }, [filteredApplicationData]);

  return (
    <div className={(isGreaterThanTablet && isSidebar) ? "landing-page hide-sidebar" : "landing-page sidebar"}>
      <BCModal
        isModalOpen={isExportSectionIncompleteModal}
        onClose={() => setIsExportSectionIncompleteModal(false)}
        size="small"
        closeIcon
        header="Warning, missing data!"
        content={<>There's information missing required to export the Workbook. Please review the following section(s): {convertArrayToOxfordString(invalidExportSections, "and", "")}.</>}
        actions={<Grid>
          <Grid.Row columns={2}>
            <Grid.Column>
              <Button icon fluid onClick={() => setIsExportSectionIncompleteModal(false)}>
                <div className="gmr-button-container">
                  <div className="gmr-button-text">Cancel</div>
                </div>
              </Button>
            </Grid.Column>
            <Grid.Column>
              <Button 
                className="bp-button" 
                style={setBackgroundColor("#EED202")}
                fluid 
                onClick={() => handleSummary(filterBySearchValue(applicationInfolist), exportSectionApplicationId)}
                >
                <div className="gmr-button-container">
                  <div className="gmr-button-text">Review Application</div>
                </div>
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>}
        brokerColor="#EED202"
      />
      {<a className="export-link" href={exportWorkbookUrl} download ref={exportWorkbookRef as any}>0</a>}
      {isLoading ? <Loader className="" active inline="centered" size={"big"} /> : <div>
        <Card.Group>
          <BCMessage
            animation={"fly down"}
            duration={750}
            visible={isMessage}
            transitionOnMount
            styles="full-width"
            onDismiss={() => setIsMessage(false)}
            errorResponse={errorResponse}
          />
          <BCMenuBar
            leftChildren={[<React.Fragment>
              <div className="landing-page-menu-container">
                {isGreaterThanMobile ? <div className="landing-page-button-group">
                  {isStartApplicationPersona && <BCButton labelPosition="left" iconName="add" brokerColor={brokerColor} handleOnClick={() => handleApplication({})}>
                    <div>Start Application</div>
                  </BCButton>}
                  {(applicationInfolist && filteredApplicationData?.length !== 0) && <Checkbox 
                    className="view-toggle" 
                    toggle 
                    label={{children: isKanbanView ? "Switch to table view!" : isKanbanView === false && isGreaterThanTablet === false ? "Switch to table view!" : "Switch to card view!" }} 
                    onChange={() => handleInfolistViewType()} 
                    defaultChecked={!isKanbanView}
                  />}
                </div> : isStartApplicationPersona && <BCButton brokerColor={brokerColor} handleOnClick={() => handleApplication({})}>
                    <Icon className="add" fitted />
                </BCButton>}
              </div>
            </React.Fragment>]}
            rightChildren={[ <BCFilterSearch reducerInstance={reducerInstance} hideFilterBar={!isValidPermission(permissions, "GlobalAdminAccess", "read")} />]}
            className={isValidPermission(permissions, "GlobalAdminAccess", "read") && isFilterOpen ? "no-bottom-margin" : ""}
          />
          
          {isValidPermission(permissions, "GlobalAdminAccess", "read") && <BCFilterBar reducerInstance={reducerInstance}/>}
        </Card.Group>
        {applicationInfolist && filteredApplicationData?.length === 0 ?
          <Card.Group>
            <Card fluid>
              <div className="landing-page-empty-container">
                <div className="landing-page-empty-content">
                  {isValidPermission(permissions, "BrokerBorrowerApplication", "read") && <Image className="landing-page-empty-icon center" src={BCQuoteIcon} />}
                  <div className="landing-page-empty-text">Looks like there isn't any pricing requests! Click below and get your loan priced in five minutes or less.</div>
                  <div className="center">
                    {isValidPermission(permissions, "BrokerBorrowerApplication", "read") &&<BCButton brokerColor={brokerColor} handleOnClick={() => handleApplication({})}><div>Get Started</div></BCButton>}
                  </div>
                </div>
              </div>
            </Card>
          </Card.Group> : isKanbanView === true && isGreaterThanMobile ? 
          <div className="broker-kanban-container">
            <BCKanban
              data={kanbanViews && kanbanViews[0] ? sortedApplicationsKanban : []}
              setData={setApplicationInfolist}
              freezeLanes
              isGreaterThanMobile={isGreaterThanMobile} 
              infoData={filteredApplicationData}
              handleInfo={handleApplication} 
              handleCopyApplication={handleCopyApplication}
              handleApplication={handleApplication}
              handleSummary={(id) => handleSummary(filterBySearchValue(applicationInfolist), id)}
              ellipsis
              labels={[
                {
                  type: "progress",
                  condition: "submittedOn",
                  text: "In Progress",
                  color: "yellow"
                },
                {
                  type: "completed",
                  completed: true,
                  condition: "submittedOn",
                  text: "Completed",
                  color: "green"
                }
              ]}
              disableSummary
              disableApplication
              brokerColor={brokerColor}
              ellipsisHandlers={isInternalUser ? [
                {
                  action: (_, item) => handleExportWorkbook(item),
                  text: "Export Workbook"
                }
              ] : []}
            />
          </div> : isKanbanView === true && isGreaterThanTablet === false ? <BCCards 
            isGreaterThanMobile={isGreaterThanMobile} 
            infoData={applicationInfolist} 
            handleInfo={handleApplication} 
            displayList={filteredApplicationData}
            handleApplication={handleApplication}
            handleCopyApplication={handleCopyApplication}
            handleSummary={(id) => handleSummary(filterBySearchValue(applicationInfolist), id)}
            ellipsis
            labels={[
              {
                type: "progress",
                condition: "submittedOn",
                text: "In Progress",
                color: "yellow"
              },
              {
                type: "completed",
                completed: true,
                condition: "submittedOn",
                text: "Completed",
                color: "green"
              }
            ]}
            isKanban={false}
            disableSummary
            disableApplication
            brokerColor={brokerColor}
            ellipsisHandlers={isInternalUser ? [
              {
                action: (_, item) => handleExportWorkbook(item),
                text: "Export Workbook"
              }
            ] : []}
          /> : isKanbanView === false ? <React.Fragment>{<BCTable
              data={convertToTableFormat(filteredApplicationData)} 
              dataRules={[{ field: "id", rule: "hide" }]}
              handleClickItem={(item) => item.id ? handleSummary(filterBySearchValue(applicationInfolist), item.id) : void 0}
              selectable
              handleApplication={handleApplication}
              handleCopyApplication={handleCopyApplication}
              brokerColor={brokerColor}   
              handleSummary={(id) => handleSummary(filterBySearchValue(applicationInfolist), id)}
              ellipsis
              disableSummary
              disableApplication
              sortable={["Closing Date", "Name", "Loan Program", "Submitted On", "Properties or Units"]}
              pagination={25} 
              ellipsisHandlers={isInternalUser ? [
                {
                  action: (_, item) => handleExportWorkbook(item),
                  text: "Export Workbook"
                }
              ] : []}
              editMode
            />}  
          </React.Fragment> : null
        }
      </div>}
    </div>
  );

};

export default BPApplications;
